<template>
  <div class="w-full p-4">
    <!-- The band stays on screen, as on My Expenses: title, the one line of
         context and the filters travel together. -mt-4 cancels the page's own
         p-4, which otherwise leaves 16px of slack before it catches. -->
    <div ref="topStickyRef" class="sticky top-14 z-30 bg-gray-200 -mx-4 -mt-4 px-4 pt-3 pb-4">
      <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-2">
        <h2 class="text-lg font-semibold">Operational Runbook</h2>
      </div>

      <p class="text-sm text-gray-500">
        Recurring work by department — is today's work done?
      </p>

      <div class="mt-3 flex items-center gap-3">
        <label class="text-sm text-gray-600 flex items-center">
          As of
          <input
            type="date"
            v-model="asOf"
            class="ml-1 h-9 px-2 border border-gray-400 rounded-md text-sm"
            @change="load"
          />
        </label>
        <select
          v-model="departmentId"
          class="h-9 px-2 border border-gray-400 rounded-md text-sm"
          @change="load"
        >
          <option :value="''">All departments</option>
          <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
        </select>
      </div>
    </div>

    <p v-if="loading" class="mt-6 text-gray-500">Loading…</p>
    <p v-else-if="error" class="mt-6 text-red-600">{{ error }}</p>

    <template v-else>
      <div class="mt-4 flex gap-4 text-sm">
        <span class="px-3 py-1 rounded bg-gray-100">
          {{ summary.due }} due
        </span>
        <span class="px-3 py-1 rounded bg-green-100 text-green-800">
          {{ summary.done }} done
        </span>
        <span v-if="summary.missed" class="px-3 py-1 rounded bg-red-100 text-red-800">
          {{ summary.missed }} with misses
        </span>
      </div>

      <p v-if="!tasks.length" class="mt-6 text-gray-500">
        No active recurring tasks{{ departmentId ? ' for this department' : '' }} yet.
      </p>

      <div v-for="group in grouped" :key="group.name" class="mt-6">
        <h3
          class="font-semibold text-dark-primary border-b border-gray-300 pb-1 mb-2
                 bg-gray-200 sticky z-20"
          :style="{ top: (topStickyHeight + NAVBAR_HEIGHT) + 'px' }"
        >
          {{ group.name }}
        </h3>

        <div
          v-for="row in group.rows"
          :key="row.id"
          class="border border-gray-300 rounded-md bg-white p-3 mb-2
                 flex items-start justify-between gap-4"
          :class="rowClass(row)"
        >
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-medium">{{ row.title }}</span>
              <span class="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                {{ row.cadence }}
              </span>
              <span v-if="row.sub_department_name" class="text-xs text-gray-500">
                {{ row.sub_department_name }}
              </span>
              <a
                v-if="row.reference_url"
                :href="row.reference_url"
                class="text-xs text-accent-primary underline"
              >open</a>
            </div>

            <p v-if="row.instructions" class="text-sm text-gray-600 mt-1">
              {{ row.instructions }}
            </p>

            <p class="text-xs text-gray-500 mt-1">
              <span v-if="row.owner_display">Owner: {{ row.owner_display }} · </span>
              <span v-if="row.current">Period {{ row.current.period }}</span>
              <span v-else>Not scheduled today</span>
              <span v-if="row.current && row.current.completed_by">
                · done by {{ row.current.completed_by }}
              </span>
            </p>

            <p v-if="row.misses.length" class="text-xs text-red-700 mt-1">
              Missed: {{ row.misses.map(m => m.period).join(', ') }}
            </p>
          </div>

          <div v-if="row.current" class="flex items-center gap-2 shrink-0">
            <button
              class="px-3 py-1.5 rounded-md text-sm border"
              :class="row.current.status === 'done'
                ? 'bg-green-600 text-white border-green-600'
                : 'bg-white border-gray-400 hover:bg-gray-100'"
              :disabled="saving === row.id"
              @click="toggle(row, 'done')"
            >
              {{ row.current.status === 'done' ? 'Done' : 'Mark done' }}
            </button>
            <button
              class="px-3 py-1.5 rounded-md text-sm border"
              :class="row.current.status === 'skipped'
                ? 'bg-gray-500 text-white border-gray-500'
                : 'bg-white border-gray-400 hover:bg-gray-100'"
              :disabled="saving === row.id"
              @click="toggle(row, 'skipped')"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { useStickyOffset } from '@/composables/useStickyOffset'
import { getDashboard, getDepartments, markTaskPeriod } from '@/services/BackOffice'

// The app's navbar is fixed at this height, as on the backlog board.
const NAVBAR_HEIGHT = 56

// Measured, not assumed: the band holds a filter row that wraps at narrow
// widths, and a department heading offset by a stale number overlaps it.
const topStickyRef = ref(null)
const topStickyHeight = useStickyOffset(topStickyRef)

const store = useStore()

const tasks = ref([])
const departments = ref([])
const departmentId = ref('')
const asOf = ref(new Date().toISOString().slice(0, 10))
const loading = ref(true)
const saving = ref(null)
const error = ref('')

const token = () => store.getters['auth/showToken']

const summary = computed(() => ({
  due: tasks.value.filter(t => t.current).length,
  done: tasks.value.filter(t => t.current && t.current.status === 'done').length,
  missed: tasks.value.filter(t => t.misses.length).length,
}))

const grouped = computed(() => {
  const groups = []
  for (const row of tasks.value) {
    let group = groups.find(g => g.name === row.department_name)
    if (!group) {
      group = { name: row.department_name, rows: [] }
      groups.push(group)
    }
    group.rows.push(row)
  }
  return groups
})

function rowClass(row) {
  if (row.misses.length) return 'border-red-300 bg-red-50'
  if (row.current && row.current.status === 'done') return 'border-green-200 bg-green-50'
  if (row.current && row.current.status === 'skipped') return 'border-gray-300 bg-gray-50'
  return ''
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const params = { as_of: asOf.value }
    if (departmentId.value) params.department = departmentId.value
    const data = await getDashboard(token(), params)
    tasks.value = data.tasks
  } catch (e) {
    error.value = describe(e)
  } finally {
    loading.value = false
  }
}

async function toggle(row, status) {
  // Clicking the active state again reverts the period to pending, so a
  // mis-click is undoable without an extra control.
  const next = row.current.status === status ? 'pending' : status
  saving.value = row.id
  try {
    await markTaskPeriod(token(), {
      recurring_task: row.id,
      period: row.current.period,
      status: next,
    })
    await load()
  } catch (e) {
    error.value = describe(e)
  } finally {
    saving.value = null
  }
}

function describe(e) {
  if (e?.response?.status) {
    const detail = e.response.data ? JSON.stringify(e.response.data) : ''
    return `${e.response.status} ${e.response.statusText || ''} ${detail}`.trim()
  }
  return String(e)
}

onMounted(async () => {
  try {
    departments.value = await getDepartments(token())
  } catch (e) {
    // Non-fatal: the filter just stays empty.
  }
  await load()
})
</script>
